import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Button,
  Grid,
  Link,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { Box, Container } from '@mui/system';

import DynamicBreadcrumbs from '@/components/nav/Breadcrumb';
import ResourcesRightSidebar from '@/components/nav/ResourcesRightSidebar';
import AppLayout from '@/layouts/AppLayout';
import { Meta } from '@/layouts/Meta';


const Form1095BInstructions = () => {
  return (
    <AppLayout
      meta={
        <Meta
          title="IRS Form 1095-B instructions for Tax Year 2025"
          description="Learn IRS Form 1095-B step-by-step instructions for Tax Year 2025. Understand how to report minimum essential health coverage accurately with TaxZerone."
          keywords=""
          canonical="resources/information-returns/form-1095-b-instructions/"
          ogType="article"
          jsonLdPricing="2.49"
          jsonLdAggregateRatingValue="5.00"
          jsonLdAggregateRatingCount="57448"
        />
      }
    >
      <Container maxWidth="lg" sx={{ textAlign: 'center' }}>
        <Typography
          variant="h1"
          sx={{
            fontSize: { lg: 40, md: 36, sm: 32, xs: 24 },
            fontWeight: 600,

            mt: 3,
            textAlign: 'center',
          }}
        >
          <span className="highlight-container">
            <span className="highlight"> 1095-B/1094-B</span>
          </span>{' '}
          Instructions
        </Typography>
        <Typography
          variant="body1"
          sx={{
            fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
            fontWeight: 400,
            mt: 1,
            mb: 2,
          }}
        >
          Learn about Forms 1095-B /1094-B, and file them by following the
          instructions.
        </Typography>
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Button
            variant="contained"
            className="btn-primary medium"
            sx={{
              fontSize: {
                xs: '16px !important',
                sm: '18px !important',
                lg: '24px !important',
              },
              transition: 'all 0.3s ease 0s',
              '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
            }}
            href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=1095binstructions`}
          >
            E-File Now
          </Button>
        </Box>
      </Container>
      <Grid
        container
        sx={{
          backgroundColor: '#f0f2f9',
          py: 5,
          mx: '0 !important',
          width: '100% !important',
        }}
        spacing={2}
        m={0}
        mt={0}
      >
         <Container maxWidth="lg" sx={{ mb: 0 }}>
          {process.env.NEXT_PUBLIC_ENVIRONMENT !== 'production' && (
            <DynamicBreadcrumbs />
          )}
          <Grid container spacing={0} rowSpacing={0} m={0} sx={{flexDirection:'row-reverse'}}>
            <Grid
              size={{ xs: 12, md: 3.5, sm: 4.5 }}
              sx={{ display: { xs: 'none', sm: 'block' } }}
            >
              <ResourcesRightSidebar />
            </Grid>
            <Grid size={{ xs: 12, md: 8.5, sm: 7.5 }}>
              <Paper
                variant="outlined"
                sx={{
                  border: '1px solid #ccc',
                  p: 3,
                  px: 3,
                  textAlign: 'left',
                  mb: 0,
                }}
              >
                 <Box
                  itemScope
                  itemType="https://schema.org/Article"
                  sx={{
                    p: 3,
                    px: 3,
                    backgroundColor: '#f0f5f8',
borderLeft:'5px solid #01456c',
                    mb: 4,
                    borderRadius: 3,
                    position: 'relative',
		    
                  }}
                >
                  <link
                    itemProp="mainEntityOfPage"
                    href="https://www.taxzerone.com/resources/information-returns/form-1095-b-instructions/"
                  />

                  {/* Article requires an image */}
                  <div
                    itemProp="image"
                    itemScope
                    itemType="https://schema.org/ImageObject"
                    style={{ display: 'none' }}
                  >
                    <link
                      itemProp="url"
                      href="https://www.taxzerone.com/assets/images/logo.webp"
                    />
                    <data itemProp="width" value="380" />
                    <data itemProp="height" value="124" />
                  </div>

                  <Box
                    sx={{
                      display: 'grid',
                      justifyContent: 'flex-start',
                      alignItems: 'center',
                      textAlign: 'left',
                      mb: 2,
                    }}
                  >
                    <Typography variant="body2">
                      Last updated:{' '}
                      <time
                        itemProp="datePublished"
                        dateTime="2026-07-07T09:00:00-05:00"
                      >
                        July 7, 2026
                      </time>
                      <time
                        itemProp="dateModified"
                        dateTime="2026-07-07T09:00:00-05:00"
                        style={{ display: 'none' }}
                      >
                        July 7, 2026
                      </time>
                    </Typography>

                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 600 }}
                      itemProp="author"
                      itemScope
                      itemType="https://schema.org/Person"
                    >
                      -by{' '}
                      <span itemProp="name">
                        <a
                          itemProp="url"
                          href="https://www.taxzerone.com/"
                          style={{ color: 'inherit', textDecoration: 'none' }}
                        >
                          Alexia Zepeda
                        </a>
                      </span>{' '}
                      | TaxZerone
                    </Typography>
                  </Box>

                  <Typography
                    variant="h4"
                    itemProp="headline"
                    sx={{
                      fontWeight: 600,
                      mb: 2,
                      fontSize: { md: 20, sm: 18, xs: 16 },
                      color: '#01456c',
                      fontStyle: 'italic',
                    }}
                  >
                    Form 1095-B at a Glance
                  </Typography>

                  <Box
                    component="ul"
                    itemScope
                    itemType="https://schema.org/ItemList"
                    sx={{
                      mb: 0,
                      pl: 0,
                      listStyle: 'none',

                      '& li': {
                        mb: 1.6,
                        position: 'relative',
                        pl: 3,
                      },

                      '& li::before': {
                        content: '"★"',
                        position: 'absolute',
                        left: 0,
                        top: '-2px',
                        color: '#01456c',
                        fontSize: '18px',
                      },
                    }}
                  >
                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <data
                        itemProp="position"
                        value="1"
                        style={{ display: 'none' }}
                      />
                      <span itemProp="name">
                        <strong style={{ fontWeight: 600 }}>Form 1095-B</strong>
                        {
                          ' reports information about minimum essential coverage (MEC) provided to individuals under the Affordable Care Act (ACA). Health insurance providers, government agencies, and certain employers use this form to report individuals who were covered, the months of coverage, and other required health coverage details to the IRS and covered individuals.'
                        }
                      </span>
                    </li>
                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <data
                        itemProp="position"
                        value="2"
                        style={{ display: 'none' }}
                      />
                      <span itemProp="name">
                        <strong style={{ fontWeight: 600 }}>
                          Recipient copies
                        </strong>
                        {' must '}
                        generally be furnished
                        {' by '}
                        <strong style={{ fontWeight: 600 }}>
                          {'February 28 '}
                        </strong>
                        {'following the calendar year being reported. The '}
                        <strong style={{ fontWeight: 600 }}>
                          IRS filing deadline
                        </strong>
                        {' is '}
                        <strong style={{ fontWeight: 600 }}>February 28</strong>
                        {' for paper filings and '}
                        <strong style={{ fontWeight: 600 }}>March 31</strong>
                        {' for electronic filings.'}
                      </span>
                    </li>
                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <data
                        itemProp="position"
                        value="3"
                        style={{ display: 'none' }}
                      />
                      <span itemProp="name">
                        {'This article covers '}
                        <strong style={{ fontWeight: 600 }}>
                          line-by-line instructions
                        </strong>
                        {', including '}
                        <strong style={{ fontWeight: 600 }}>{'Part I '}</strong>
                        (Responsible Individual)
                        {', including '}
                        Responsible Individual Name, Social Security Number
                        (SSN) or Date of Birth, and Address Information
                        {','}{' '}
                        <strong style={{ fontWeight: 600 }}>
                          {'Part II '}
                        </strong>
                        (Information About Certain Employer-Sponsored Coverage)
                        {', including '}
                        Employer Name, Employer Identification Number (EIN), and
                        Employer Contact Information ,
                        <strong style={{ fontWeight: 600 }}> </strong>
                        <strong style={{ fontWeight: 600 }}>Part III</strong>
                        {' (Issuer or Other Coverage Provider)'}
                        {', including '}
                        Issuer or Other Coverage Provider Name, EIN, Contact
                        Information, and Address
                        {', and '}
                        <strong style={{ fontWeight: 600 }}>Part IV</strong>
                        (Covered Individuals)
                        {', including '}
                        Covered Individual Name, Social Security Number or Date
                        of Birth, and Months of Coverage .
                      </span>
                    </li>
                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <data
                        itemProp="position"
                        value="4"
                        style={{ display: 'none' }}
                      />
                      <span itemProp="name">
                        <strong style={{ fontWeight: 600 }}>Penalties:</strong>
                        {' Late, incorrect, or incomplete filing of '}
                        <strong style={{ fontWeight: 600 }}>Form 1095-B</strong>
                        {' may result in IRS penalties ranging from '}
                        <strong style={{ fontWeight: 600 }}>
                          $60 to $680 per return
                        </strong>
                        , depending on the delay, correction requirements, and
                        whether the filer meets electronic filing obligations.
                      </span>
                    </li>
                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <data
                        itemProp="position"
                        value="5"
                        style={{ display: 'none' }}
                      />
                      <span itemProp="name">
                        <strong style={{ fontWeight: 600 }}>Extensions:</strong>{' '}
                        <Link
                          href="/tax-extension/efile-irs-form-8809-online/"
                          sx={{ color: '#0202ff' }}
                        >
                          <strong style={{ fontWeight: 600 }}>
                            File Form 8809
                          </strong>
                        </Link>
                        {' to request an automatic filing extension. '}
                        <Link
                          href="/tax-extension/file-irs-form-15397-online/"
                          sx={{ color: '#0202ff' }}
                        >
                          <strong style={{ fontWeight: 600 }}>
                            File Form 15397
                          </strong>
                        </Link>{' '}
                        to
                        {' request the full '}
                        <strong style={{ fontWeight: 600 }}>
                          30-day extension
                        </strong>
                        {' for furnishing recipient copies.'}
                      </span>
                    </li>
                  </Box>

                  <div
                    itemProp="publisher"
                    itemScope
                    itemType="https://schema.org/Organization"
                    style={{ display: 'none' }}
                  >
                    <span itemProp="name">TaxZerone</span>
                    <div
                      itemProp="logo"
                      itemScope
                      itemType="https://schema.org/ImageObject"
                    >
                      <link
                        itemProp="url"
                        href="https://www.taxzerone.com/assets/images/logo.webp"
                      />
                      <data itemProp="width" value="380" />
                      <data itemProp="height" value="124" />
                    </div>
                  </div>
                </Box>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Form 1095-B is an information return form used to report
                  individuals covered by Minimum Essential Coverage (including
                  coverage for Spouse and Dependents). Minimum Essential
                  Coverage includes government-sponsored programs, eligible
                  employer-sponsored plans, individual market plans, and other
                  coverage that the Department of Health and Human Services
                  designates as minimum essential coverage. Form 1095-B should
                  be filed along with Form 1094-B to stay compliant with IRS
                  regulations.
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Who must file Form 1095-B?
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Health Coverage Providers
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Organizations providing essential health coverage to
                  individuals must{' '}
                  <Link
                    href="/aca-forms/e-file-form-1095b/"
                    sx={{ color: '#0202ff' }}
                  >
                    {' '}
                    file Form 1095-B
                  </Link>
                  , using Form 1094-B to submit these returns.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Self-Insured Employers
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Employers with their own health plans (including individual
                  coverage HRAs) must report coverage. Large employers (50+
                  employees) usually report this on Form 1095-C but can use Form
                  1095-B for nonemployees.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Small Employers with Self-Insured Plans
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Small employers not subject to shared responsibility rules use
                  Forms 1094-B and 1095-B to report coverage.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Health Insurance Issuers and Carriers
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Insurers file Form 1095-B for most employer-sponsored and
                  individual health plans, including coverage through the Small
                  Business Health Options Program (SHOP).
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Plan Sponsors of Self-Insured Employer Coverage
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Employers, multiemployer plan sponsors, and employee
                  organizations offering self-insured coverage must file Form
                  1095-B unless required to use Form 1095-C.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Government Employers
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Government entities may be designated to report for a
                  government employer providing self-insured plans.
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  {' '}
                  Government-Sponsored Program Administrators{' '}
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  {' '}
                  Agencies administering programs like Medicare Part A,
                  Medicaid, CHIP, TRICARE, Veterans Affairs healthcare coverage,
                  and Peace Corps coverage must file Form 1095-B.{' '}
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  State Agencies
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  State agencies report coverage for Medicaid, CHIP, and other
                  government programs.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Designated Health Plans
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Providers of designated essential coverage plans, such as
                  Medicare Advantage, Refugee Medical Assistance, and Basic
                  Health Programs, must file Form 1095-B.
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  Important dates for filing
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Paper Filing with the IRS
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  IRS allows up to 10 information reports to be filed using the
                  paper filing method for a tax year.
                </Typography>
                <Box className="deadline-dates" sx={{}}>
                  <Typography
                    variant="body1"
                    sx={{
                      p: 2,
                      color: '#fff',
                      fontSize: 18,
                      fontWeight: 500,
                      mt: 0,
                    }}
                  >
                    <b style={{ fontSize: 22 }}>
                      {' '}
                      Deadline for the Tax year 2025 :
                    </b>{' '}
                    March 02, 2026
                  </Typography>
                </Box>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                    mt: 1,
                  }}
                >
                  Electronic Filing with the IRS
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  IRS encourages all to E-File form 1095-B/1094-B even if they
                  are qualified for paper filing.
                </Typography>
                <Box className="deadline-dates" sx={{}}>
                  <Typography
                    variant="body1"
                    sx={{
                      p: 2,
                      color: '#fff',
                      fontSize: 18,
                      fontWeight: 500,
                      mt: 0,
                    }}
                  >
                    <b style={{ fontSize: 22 }}>
                      {' '}
                      Deadline for the Tax year 2025 :
                    </b>{' '}
                    March 31, 2026
                  </Typography>
                </Box>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                    mt: 1,
                  }}
                >
                  Send Individual copies
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Send recipient copies to the respective individuals,
                  regardless of your filing method (Paper and E-filing).
                </Typography>
                <Box className="deadline-dates" sx={{}}>
                  <Typography
                    variant="body1"
                    sx={{
                      p: 2,
                      color: '#fff',
                      fontSize: 18,
                      fontWeight: 500,
                      mt: 0,
                    }}
                  >
                    <b style={{ fontSize: 22 }}>
                      {' '}
                      Deadline for the Tax year 2025 :
                    </b>{' '}
                    March 02, 2026
                  </Typography>
                </Box>
                <Alert
                  severity="warning"
                  icon={false}
                  sx={{
                    mt: 3,
                    // ml: { sm: 5, xs: 1 },
                    // mr: { sm: 8, xs: 1 },
                    mb: 4,
                    textAlign: 'left',
                    borderLeft: '3px solid rgb(102, 60, 0)',
                  }}
                >
                  <b>Note:</b>{' '}
                  <Box component="ul" sx={{ mb: 1, '& li': { mb: 0.6 } }}>
                    <li>
                      If you are filing more than 10 forms, you must file
                      electronically.
                    </li>
                    <li>
                      If the filing deadline falls on a weekend or holiday, the
                      next working day will be considered as the final
                      date.{' '}
                    </li>
                  </Box>
                </Alert>
                <Box
                  component="div"
                  sx={{ mb: 2, p: 2, borderRadius: 2 }}
                  className="gradient-bg"
                >
                  <Stack
                    direction={{ xs: 'column', md: 'row' }}
                    spacing={2}
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Box sx={{ width: '100%' }}>
                      <Typography
                        variant="body1"
                        sx={{ fontWeight: 600, mb: 1 }}
                      >
                        TaxZerone encourages E-filing to achieve more efficiency
                        and accuracy
                      </Typography>
                    </Box>
                    <Box
                      sx={{ width: '100%', maxWidth: 130, textAlign: 'center' }}
                    >
                      <Button
                        variant="contained"
                        sx={{
                          textTransform: 'none',
                          boxShadow: 'none',
                          borderRadius: 2,
                          backgroundColor: '#fff',
                          color: '#333',
                          '&:hover, &:focus, &:active': {
                            color: '#fff',
                            background: '#172b6b',
                          },
                        }}
                        size="large"
                        href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=1095binstructions`}
                      >
                        E-File Now
                      </Button>
                    </Box>
                  </Stack>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Form 1095-B Penalty
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>
                    If an employer does not file Form 1095-B by the deadline,
                    the IRS will impose a penalty for failing to file accurate
                    information returns and furnish correct payee statements.
                    The penalty begins at $340 per return, with a maximum limit
                    of $4,098,500 per year.{' '}
                  </li>
                  <li>
                    If the requirement to file the returns and furnish recipient
                    statements is intentionally ignored, the penalty per return
                    increases to $680, with no annual maximum limitation.{' '}
                  </li>
                </Box>
                <Box
                  component="div"
                  sx={{
                    mb: 5,
                    p: 2,
                    borderRadius: 2,
                    backgroundColor: '#f9f9f9',
                    backgroundSize: {
                      xs: '20% auto',
                      sm: '20% auto',
                      md: '20% auto',
                    },
                    border: '1px solid #ccc',
                  }}
                >
                  <Stack
                    direction={{ md: 'row', sm: 'row', xs: 'column' }}
                    sx={{
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}
                  >
                    <Box sx={{ width: 900, maxWidth: '100%' }}>
                      {/* <Typography
                        variant="h4"
                        sx={{
                          fontWeight: 600,
                          mb: 1,
                          fontSize: '24px !important',
                        }}
                      >
                        Why Risk penalties?
                      </Typography> */}
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 600,
                          fontSize: { lg: 17, md: 18, sm: 16 },
                        }}
                      >
                        Skip the last-minute hurry and E-file your taxes today!
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        width: 250,
                        maxWidth: '100%',
                        textAlign: { xs: 'center', sm: 'right' },
                        mt: 0,
                      }}
                    >
                      <Button
                        variant="contained"
                        color="primary"
                        sx={{
                          textTransform: 'none',
                          boxShadow: 'none',
                          borderRadius: 2,
                        }}
                        size="large"
                        href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=1095binstructions`}
                      >
                        E-File Today
                      </Button>
                    </Box>
                  </Stack>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 1.5,
                  }}
                >
                  Instructions to file Form 1095-B and Form 1094-B
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 2,
                    fontWeight: 600,
                  }}
                >
                  Form 1094-B: Transmittal of Health Coverage Information
                  Returns
                </Typography>{' '}
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Form 1094-B, Transmittal of Health Coverage Information
                  Returns, acts as a cover sheet for Form 1095-B submissions to
                  the IRS.{' '}
                </Typography>
                <Box sx={{ border: '1px solid #333', mb: 1 }}>
                  <picture>
                    <source srcSet="/assets/images/health-coverage-1094-b-img.webp" />
                    <source srcSet="/assets/images/health-coverage-1094-b-img.png" />
                    <img
                      src="/assets/images/health-coverage-1094-b-img.png"
                      loading="lazy"
                      alt="Form 1094-B: Transmittal of Health Coverage Information Returns "
                      width={760}
                      height={245}
                      className="proportional-img"
                    />
                  </picture>
                </Box>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  <span style={{ fontWeight: 600 }}> Line 1:</span> Enter the
                  Filer’s Name (Full Name)
                </Typography>
                <Typography variant="body1" sx={{ mb: 1.5 }}>
                  <span style={{ fontWeight: 600 }}> Line 2:</span> Enter the
                  Employer identification number (EIN)
                </Typography>
                <Typography variant="body1" sx={{ mb: 1.5 }}>
                  <span style={{ fontWeight: 600 }}> Line 3 :</span> Enter the
                  Person’s Name to contact, if the IRS wishes to contact the
                  individual about the filing errors and provide any additional
                  information.
                </Typography>
                <Typography variant="body1" sx={{ mb: 1.5 }}>
                  <span style={{ fontWeight: 600 }}> Line 4:</span> Enter the
                  contact person’s phone number
                </Typography>
                <Typography variant="body1" sx={{ mb: 1.5 }}>
                  <span style={{ fontWeight: 600 }}> Lines 5 through 8:</span>{' '}
                  Enter the filer&apos;s Complete address, City, State, Country,
                  and Zip Code. queries
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  <span style={{ fontWeight: 600 }}> Lines 9:</span> Enter the
                  total number of Form 1095-B submitted with Form 1094-B,
                  Transmittal form.
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 1.5,
                  }}
                >
                  Form 1095-B: Health Coverage
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 1,
                    fontWeight: 600,
                  }}
                >
                  Part I - Responsible Individual
                </Typography>
                <Box sx={{ border: '1px solid #333', mb: 1 }}>
                  <picture>
                    <source srcSet="/assets/images/responsible-individual-1095-b-img.webp" />
                    <source srcSet="/assets/images/responsible-individual-1095-b-img.png" />
                    <img
                      src="/assets/images/responsible-individual-1095-b-img.png"
                      loading="lazy"
                      alt="Part I - Responsible Individual "
                      width={751}
                      height={146}
                      className="proportional-img"
                    />
                  </picture>
                </Box>
                <Typography variant="body1" sx={{ mb: 1.5 }}>
                  <span style={{ fontWeight: 600 }}> Line 1:</span> Enter the
                  Responsible Individual&apos;s (Recipient of Health Coverage)
                  First name, middle name, and Last name.
                </Typography>
                <Typography variant="body1" sx={{ mb: 1.5 }}>
                  <span style={{ fontWeight: 600 }}> Line 2:</span> Enter the
                  9-digit SSN (Social Security Number) of the responsible
                  individual. If the SSN is not available, enter the other
                  Taxpayer Identification Number(TIN.)
                </Typography>
                <Typography variant="body1" sx={{ mb: 1.5 }}>
                  <span style={{ fontWeight: 600 }}> Line 3:</span> Enter the
                  Date of Birth in MM/DD/YYYY format of the individual, Date of
                  Birth is needed only when Line 2 is blank.
                </Typography>
                <Typography variant="body1" sx={{ mb: 1.5 }}>
                  <span style={{ fontWeight: 600 }}> Lines 4 through 7:</span>{' '}
                  Enter the complete mailing address including street address
                  (including apartment number), city, state, country, and Zip
                  code.
                </Typography>
                <Typography variant="body1" sx={{ mb: 1.5 }}>
                  <span style={{ fontWeight: 600 }}> Lines 8:</span> Enter the
                  code to identify the origin of health coverage offered to you
                  or other covered individuals. It is used to determine the type
                  of coverage offered. Only one code should be entered on this
                  line.
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  <span style={{ fontWeight: 600 }}> A- </span>Small Business
                  Health Options Program (SHOP)
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  <span style={{ fontWeight: 600 }}> B- </span>
                  Employer-sponsored coverage, except for individual coverage
                  Health Reimbursement Arrangement (HRA)
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  <span style={{ fontWeight: 600 }}> C- </span>
                  Government-sponsored program
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  <span style={{ fontWeight: 600 }}> D- </span>
                  Individual market insurance
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  <span style={{ fontWeight: 600 }}> E- </span>
                  Multiemployer plan
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  <span style={{ fontWeight: 600 }}> F- </span>
                  Other designated minimum essential coverage
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  <span style={{ fontWeight: 600 }}> G- </span>
                  Employer-sponsored coverage including individual coverage HRA
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  <span style={{ fontWeight: 600 }}> line 9: </span>
                  Leave it blank, it is reserved for future use.
                </Typography>
                <Alert
                  severity="warning"
                  icon={false}
                  sx={{
                    // mt: 0,
                    // ml: { sm: 5, xs: 1 },
                    // mr: { sm: 8, xs: 1 },
                    mb: 4,
                    textAlign: 'left',
                    borderLeft: '3px solid rgb(102, 60, 0)',
                  }}
                >
                  <b>Note:</b> If you entered other than code A or B on line 8,
                  you don’t need to fill in Part II. Employers reporting
                  self-insured group health plan coverage on Form 1095-B also
                  fill B on line 8 but need not fill Part ll
                </Alert>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 1,
                    fontWeight: 600,
                  }}
                >
                  Part II – Information About Certain Employer-Sponsored
                  Coverage
                </Typography>
                <Box sx={{ border: '1px solid #333', mb: 2 }}>
                  <picture>
                    <source srcSet="/assets/images/certain-employer-1095-b-img.webp" />
                    <source srcSet="/assets/images/certain-employer-1095-b-img.png" />
                    <img
                      src="/assets/images/certain-employer-1095-b-img.png"
                      loading="lazy"
                      alt="Part II – Information About Certain Employer-Sponsored Coverage "
                      width={760}
                      height={65}
                      className="proportional-img"
                    />
                  </picture>
                </Box>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Only issuers or carriers of insured group health plans,
                  including those whose coverage was purchased through the Small
                  Business Health Options Program (SHOP), are required to
                  complete this section.
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  <span style={{ fontWeight: 600 }}> line 10: </span>
                  Enter the Employer name
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  <span style={{ fontWeight: 600 }}> line 11: </span>
                  Enter the Employer Identification Number (EIN)
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  <span style={{ fontWeight: 600 }}> Line 12 through 15: </span>
                  Enter the complete address, city, state, country, and Zip code
                  of the employer.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Part III – Issuer or Other Coverage Provider
                </Typography>
                <Box sx={{ border: '1px solid #333', mb: 1 }}>
                  <picture>
                    <source srcSet="/assets/images/coverage-provider-1095-b-img.webp" />
                    <source srcSet="/assets/images/coverage-provider-1095-b-img.png" />
                    <img
                      src="/assets/images/coverage-provider-1095-b-img.png"
                      loading="lazy"
                      alt="Part III – Issuer or Other Coverage Provider "
                      width={760}
                      height={66}
                      className="proportional-img"
                    />
                  </picture>
                </Box>
                <Typography variant="body1" sx={{ mb: 1.5 }}>
                  <span style={{ fontWeight: 600 }}> Line 16:</span> Enter the
                  Name of the coverage provider
                </Typography>
                <Typography variant="body1" sx={{ mb: 1.5 }}>
                  <span style={{ fontWeight: 600 }}> Line 17:</span> Enter the
                  Employer identification number (EIN)
                </Typography>
                <Typography variant="body1" sx={{ mb: 1.5 }}>
                  <span style={{ fontWeight: 600 }}> Line 18:</span> Enter the
                  Contact number of coverage provider, so that the individual
                  can call for any additional information.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  <span style={{ fontWeight: 600 }}> Line 19 through 22:</span>{' '}
                  Enter the complete address, city, state, country, and Zip code
                  of the coverage provider.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 1,
                    fontWeight: 600,
                  }}
                >
                  Part IV – Covered Individuals
                </Typography>
                <Box sx={{ border: '1px solid #333', mb: 2 }}>
                  <picture>
                    <source srcSet="/assets/images/covered-individuals -1095-b-img.webp" />
                    <source srcSet="/assets/images/covered-individuals -1095-b-img.png" />
                    <img
                      src="/assets/images/covered-individuals -1095-b-img.png"
                      loading="lazy"
                      alt="Part IV – Covered Individuals "
                      width={760}
                      height={274}
                      className="proportional-img"
                    />
                  </picture>
                </Box>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}> Line 23 through 28:</span>{' '}
                  Enter all the covered individual details
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}> Column (a) – </span> Enter
                  the complete name of the covered individual
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}> Column (b) – </span> Enter
                  SSN or TIN
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}> Column (c) – </span> Enter
                  date of birth, if SSN or TIN is not available
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}> Column (d) – </span> If the
                  individual was covered for at least one day every month for
                  each of the 12 months of the calendar year.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}> Column (e) – </span> If the
                  individual was not covered for the full 12 months, check the
                  appropriate box(es) for the month(s) in which the person was
                  covered for at least one day.
                </Typography>
                <Alert severity="info" icon={false} sx={{ borderRadius: 2 }}>
                  <Accordion
                    defaultExpanded
                    sx={{
                      '&.MuiAccordion-root::before, &.MuiAccordion-root.Mui-expanded::before':
                        {
                          background: 'none !important',
                        },
                      '&.MuiAccordion-root': { background: 'none !important' },
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      aria-controls=""
                      id=""
                      sx={{
                        px: 0,
                        m: 0,
                        justifyContent: 'flex-start',
                        '& .MuiAccordionSummary-content, & .MuiAccordionSummary-content.Mui-expanded':
                          { m: '0 !important', flexGrow: 0 },
                      }}
                    >
                      <Typography
                        variant="body1"
                        sx={{
                          fontWeight: 600,
                          color: '#014361',
                          fontSize: 16,
                        }}
                      >
                        Sources and References
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={{ p: 0 }}>
                      <Typography
                        component="div"
                        variant="body2"
                        sx={{ mt: 1 }}
                      >
                        The information provided is based on guidelines from the
                        IRS website. For more information, check the{' '}
                        <Link
                          href="https://www.irs.gov/pub/irs-pdf/i109495b.pdf"
                          sx={{ color: '#0202ff' }}
                          target="_blank"
                        >
                          IRS instructions for form 1095-B
                        </Link>
                        .
                      </Typography>
                    </AccordionDetails>
                  </Accordion>
                </Alert>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Grid>

      <Container maxWidth="lg" sx={{ mb: 5, my: { xs: 6, sm: 6, md: 6 } }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={{ xs: 2, md: 5 }}
        >
          <Box
            sx={{
              width: '100%',
              textAlign: 'center',
            }}
          >
            <Typography
              variant="h2"
              sx={{
                fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                fontWeight: 600,
                mb: 2,
                textAlign: 'center',
                lineHeight: 1.5,
              }}
            >
              TaxZerone Simplify the form 1095-B for just $2.49
            </Typography>
            <Typography
              sx={{
                fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
                fontWeight: 500,
                color: '#333333',
                mb: 2,
              }}
            >
              File more! Save Big!
            </Typography>
            <Typography
              variant="body1"
              sx={{ mb: 2, fontSize: { lg: 18, md: 18, sm: 16, xs: 16 } }}
            >
              The filing cost of each form can go down up to <b>$0.59</b>
            </Typography>

            <Box sx={{ textAlign: { xs: 'center', md: 'center' } }}>
              <Button
                variant="contained"
                sx={{
                  mt: 1,
                  transition: 'all 0.3s ease 0s',
                  '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
                }}
                className="btn-primary"
                href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=1095binstructions`}
              >
                File Form 1095-B/1094-B Now
              </Button>
            </Box>
          </Box>
        </Stack>
      </Container>
    </AppLayout>
  );
};

export default Form1095BInstructions;
